/**
 * @fileoverview Session self-management API calls.
 * @description The identity backend's "session self-management" group — listing and revoking the
 * caller's own sessions, signing out of all of them, and reading the caller's own principal — all
 * scoped to whoever the access token belongs to (no id parameter needed for "self").
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { getCapabilityGrants } from '@/features/auth/services/capabilities.service'
import { resolveCapabilityAccess } from '@/features/auth/utils/capability-access.utils'
import { httpClient } from '@/shared/api/http-client'

/**
 * @description A banking-role user's data lock, if any — "branch" pins them to exactly one branch
 * (e.g. a Branch Manager); "region" restricts them to one region's branches plus that region's own
 * aggregate (e.g. a Regional Manager). Absent entirely for every other role (unrestricted, bank-wide
 * access). The two location names are spelled out directly here (not imported from
 * `@/features/banking`'s own `Branch['location']`) so this shared session type has zero dependency
 * on a feature's internals, per ADR-0002 — `features/banking`'s own `resolveBankingAccessScope`
 * gives these ids banking-specific meaning.
 */
export type BankingScope =
  { type: 'branch'; branchId: string } | { type: 'region'; location: 'Pune' | 'Pimpri-Chinchwad' }

/** The session the caller's access token belongs to (CurrentUserSessionResponseDto). */
export interface CurrentUserSession {
  sessionId: string
  /** The server-owned device record — not this browser's own clientDeviceId. */
  deviceId: string
  authenticationMethod: string
  mfaVerifiedAt: string
  absoluteExpiresAt: string
  idleExpiresAt: string | null
}

/**
 * @description The signed-in user's identity and access. The first six fields are GET
 * /identity/me (CurrentUserIdentityContextResponseDto). activeRoleKey, activeRoleName and
 * privileges come from GET /authorization/me/capabilities, translated by
 * capability-access.utils.ts; they're absent only while a password change is pending (that
 * endpoint answers 403 / 1043 until then). tenantId, activeRoleLevel and bankingScope aren't served
 * by the backend at all yet — only the mock /identity/me returns them — so treat them as possibly
 * absent.
 */
export interface CurrentPrincipal {
  userId: string
  email: string
  /** First, optional middle and last name. */
  displayName: string
  status: string
  /** When true, only this endpoint and Change Password are usable (403 / appCode 1043). */
  mustChangePassword: boolean
  /** Null only while a password change is required. */
  session: CurrentUserSession | null
  tenantId?: string
  activeRoleKey?: string
  activeRoleName?: string
  activeRoleLevel?: number
  privileges?: string[]
  bankingScope?: BankingScope
}

export interface SessionSummary {
  sessionId: string
  deviceId: string
  deviceName: string | null
  startedAt: string
  lastActivityAt: string
  absoluteExpiresAt: string
  idleExpiresAt: string | null
  isCurrent: boolean
}

export interface LogoutAllResponse {
  revokedSessionCount: number
  reauthenticationRequired: true
}

/**
 * @description Reads the caller's own principal — who they are (GET /identity/me) and what they
 * may do (GET /authorization/me/capabilities, the post-login bootstrap). Capabilities are skipped
 * while a password change is pending, since that session may call nothing but /identity/me and
 * Change Password.
 * @returns {Promise<CurrentPrincipal>} The caller's identity, role and privileges.
 */
export async function getCurrentPrincipal(): Promise<CurrentPrincipal> {
  const { data: identity } = await httpClient.get<CurrentPrincipal>('/identity/me', {
    skipAccessRefresh: true,
  })

  if (identity.mustChangePassword) {
    return identity
  }

  const grants = await getCapabilityGrants()
  return { ...identity, ...resolveCapabilityAccess(grants) }
}

/**
 * @description Lists the caller's own live sessions, flagging which one is the current request.
 * @returns {Promise<SessionSummary[]>} The caller's live sessions.
 */
export async function getSessions(): Promise<SessionSummary[]> {
  const { data } = await httpClient.get<SessionSummary[]>('/identity/sessions')
  return data
}

/**
 * @description Revokes one of the caller's own sessions.
 * @param {string} sessionId - The session to revoke.
 */
export async function revokeSession(sessionId: string): Promise<void> {
  await httpClient.delete(`/identity/sessions/${sessionId}`)
}

/**
 * @description Revokes every live session the caller holds, including the current one.
 * @returns {Promise<LogoutAllResponse>} How many sessions were revoked.
 */
export async function logoutAllSessions(): Promise<LogoutAllResponse> {
  const { data } = await httpClient.post<LogoutAllResponse>('/identity/logout-all')
  return data
}
